import { faLock } from '@fortawesome/pro-solid-svg-icons';
import type { FilterConfig, FilterOption } from '@supply-mover-2-0/core';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { FormField } from '../forms/FormField';
import { Select } from '../forms/Select';
import { useFilterLayout } from './FilterLayoutContext';

interface BaseRadioSelectProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: FilterOption[];
	/** One of `onValuesChange` / `onChange` is required; the former is current. */
	onValuesChange?: (values: FilterOption[]) => void;
	onChange?: (values: FilterOption[]) => void; // Support both prop names
	disabled?: boolean;
	isModalOpen?: boolean;
	lockedValues?: FilterOption[];
	size?: 'xs' | 'sm' | 'md' | 'lg';
	layout?: 'sidebar' | 'tray' | 'inline' | 'bar';
	isPreset?: boolean; // New prop to indicate if values are preset
}

/**
 * BaseRadioSelect Component
 *
 * The single-choice filter control: exactly one option applies at a time.
 *
 * PD-15743 — it renders as a DROPDOWN, not as a stack of radio buttons. It
 * used to draw a vertical radio list, which is what made it unusable in the
 * filter bar: a five-option filter stood five rows tall, the bar grew to fit
 * it, and on a report page — a fixed-height column whose table takes
 * "whatever height is left" — every pixel the bar gained came off the table,
 * collapsing it to a header row. That height is also why the bar layouts
 * excluded `radio` from the filters they would show inline, which left the
 * one on Price Contracts permanently buried in the tray. One control, two
 * complaints, both from the same shape.
 *
 * A dropdown is the shape the Style Guide's selection decision table already
 * prescribes for "one value, short fixed list" — it is one row tall on every
 * surface, so the control now fits the bar, the tray and the sidebar alike.
 * Selection stays single-value: the caller's contract (a one-element
 * `FilterOption` array) is unchanged, and saved searches round-trip as before.
 */
export const BaseRadioSelect: React.FC<BaseRadioSelectProps> = ({
	filterType,
	config,
	selectedValues,
	onValuesChange,
	onChange,
	disabled = false,
	isModalOpen = false,
	size,
	layout,
	isPreset = false,
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// A report page hands this control to `FilterSidebar` as a pinned child
	// without naming a layout, so the surface has to come from context for it
	// to be sized like the controls beside it. An explicit prop still wins.
	const contextLayout = useFilterLayout();
	const resolvedLayout = layout ?? contextLayout ?? 'inline';
	const resolvedSize =
		size ??
		(resolvedLayout === 'sidebar' ||
		resolvedLayout === 'tray' ||
		resolvedLayout === 'bar'
			? 'xs'
			: 'md');

	const selectedValue = selectedValues[0] ?? config.defaultValue ?? null;
	const options = React.useMemo(() => config.options ?? [], [config.options]);

	const handleValueChange = React.useCallback(
		(option: FilterOption) => {
			// Support both onValuesChange and onChange
			(onValuesChange ?? onChange)?.([option]);
		},
		[onValuesChange, onChange],
	);

	// Auto-select default value if nothing is selected
	React.useEffect(() => {
		if (selectedValue == null && config.defaultValue != null) {
			handleValueChange(config.defaultValue);
		}
	}, [config.defaultValue, selectedValue, handleValueChange]);

	// Counts ride in the option label — the radio list showed them in a badge
	// beside each row, and losing them would cost the reader the sense of how
	// much each category holds ("All Zones (447)").
	const selectOptions = React.useMemo(
		() =>
			options.map((option) => {
				const label = t(option.label !== '' ? option.label : String(option.id));
				const showCount = config.hideCount !== true && option.count != null;
				return {
					value: option.id.toString(),
					label: showCount ? `${label} (${String(option.count)})` : label,
				};
			}),
		[options, config.hideCount, t],
	);

	const isDisabled = disabled || isModalOpen || isPreset;

	const label = config.label;
	// Several callers pass an empty label deliberately (the control sits under
	// their own heading). Keep the accessible name in that case — a nameless
	// combobox is unreadable — but do not spend a row of height on it.
	const hasLabel = label !== '';

	return (
		<div
			className={cn(
				// The bar sets its own spacing; anywhere else the control keeps the
				// bottom margin the surrounding filter stacks were built around.
				resolvedLayout !== 'bar' && 'mb-4',
				// A pinned report control is not inside `FilterGroup`, so it misses
				// the `[&>div]:w-[200px]` that gives the bar its even columns. Left
				// to size itself it collapses onto its own text and the chevron
				// lands on top of the label ("All Zones (304)").
				resolvedLayout === 'bar' && 'w-[200px] shrink-0',
				(disabled || isModalOpen) && 'opacity-50 pointer-events-none',
			)}
		>
			<FormField
				name={filterType}
				size={resolvedSize}
				required={config.required}
				labelHidden={!hasLabel}
				label={
					<div className="flex items-center gap-2">
						<span>{hasLabel ? t(label) : t(filterType)}</span>
						{isPreset && (
							<Icon
								icon={faLock}
								size={resolvedSize === 'xs' ? 'xs' : 'sm'}
								className={colors.classes.textSecondary}
							/>
						)}
					</div>
				}
			>
				<div className={cn(isPreset && 'opacity-60')}>
					<Select
						name={filterType}
						options={selectOptions}
						value={selectedValue?.id.toString() ?? ''}
						onChange={(value) => {
							const option = options.find((opt) => opt.id.toString() === value);
							if (option != null) {
								handleValueChange(option);
							}
						}}
						placeholder={
							config.placeholder != null ? t(config.placeholder) : undefined
						}
						disabled={isDisabled}
						// Single-choice by definition: there is always a value, and
						// clearing one would only bounce straight back to the default
						// via the effect above.
						allowClear={false}
						size={resolvedSize}
					/>
				</div>
			</FormField>
		</div>
	);
};
